if (!customElements.get('subscription-consent')) {
  customElements.define(
    'subscription-consent',
    class SubscriptionConsent extends HTMLElement {
      connectedCallback() {
        this.productInfo = this.closest('[id^="ProductInfo-"]') || this.closest('[id^="MainProduct-"]') || document;
        this.form = this.closest('form');
        this.checkbox = this.querySelector('[data-subscription-consent-checkbox]');
        this.consentProperty = this.querySelector('[data-subscription-consent-property]');
        this.error = this.querySelector('[data-subscription-consent-error]');
        this.dialog = this.querySelector('[data-subscription-consent-dialog]');
        this.openButton = this.querySelector('[data-subscription-consent-open]');
        this.closeButton = this.querySelector('[data-subscription-consent-close]');
        this.confirmButton = this.querySelector('[data-subscription-consent-confirm]');
        this.submitButton = this.form?.querySelector('[type="submit"]');
        this.quantityInput = this.productInfo.querySelector('input[name="quantity"]');
        this.hasReviewed = false;

        try {
          this.priceMap = JSON.parse(this.querySelector('[data-subscription-price-map]')?.textContent || '{}');
        } catch (error) {
          this.priceMap = {};
          console.error('Unable to parse subscription price map.', error);
        }

        this.handleChange = this.handleChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleDialogCancel = this.handleDialogCancel.bind(this);
        this.stopInternalPropagation = this.stopInternalPropagation.bind(this);

        this.addEventListener('click', this.stopInternalPropagation);
        this.addEventListener('change', this.stopInternalPropagation);
        this.openButton?.addEventListener('click', () => this.openDialog());
        this.closeButton?.addEventListener('click', () => this.closeDialog());
        this.confirmButton?.addEventListener('click', () => this.confirmReview());
        this.checkbox?.addEventListener('change', this.handleChange);
        this.form?.addEventListener('submit', this.handleSubmit, true);
        this.submitButton?.addEventListener('click', this.handleSubmit, true);
        this.dialog?.addEventListener('cancel', this.handleDialogCancel);
        this.productInfo.addEventListener('change', this.handleChange);

        this.observer = new MutationObserver(() => this.sync());
        this.observer.observe(this.productInfo, { childList: true, subtree: true });

        this.sync();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.removeEventListener('click', this.stopInternalPropagation);
        this.removeEventListener('change', this.stopInternalPropagation);
        this.form?.removeEventListener('submit', this.handleSubmit, true);
        this.submitButton?.removeEventListener('click', this.handleSubmit, true);
        this.productInfo?.removeEventListener('change', this.handleChange);
        this.dialog?.removeEventListener('cancel', this.handleDialogCancel);
      }

      isSubscriptionSelected() {
        const subscriptionRadio = this.productInfo.querySelector('input[name="plan"][value="subscription"]');
        if (subscriptionRadio) return subscriptionRadio.checked;

        const sellingPlanInput = this.productInfo.querySelector('[name="selling_plan"]');
        return Boolean(sellingPlanInput?.value);
      }

      getSelectedPlanId() {
        const sellingPlanField = this.productInfo.querySelector('[name="selling_plan"]');
        if (sellingPlanField?.value) return String(sellingPlanField.value);

        const checkedPlan = this.productInfo.querySelector('input[name="selling_plan"]:checked');
        return checkedPlan?.value ? String(checkedPlan.value) : null;
      }

      getVariantId() {
        return String(this.form?.querySelector('input[name="id"]')?.value || '');
      }

      getUnitPrice() {
        const variantPrices = this.priceMap[this.getVariantId()] || {};
        const selectedPlanId = this.getSelectedPlanId();

        if (selectedPlanId && variantPrices[selectedPlanId] != null) {
          return Number(variantPrices[selectedPlanId]);
        }

        const firstPrice = Object.values(variantPrices)[0];
        return firstPrice == null ? null : Number(firstPrice);
      }

      formatMoney(cents) {
        const currency = this.dataset.currency || 'JPY';
        const usesMinorUnits = !['BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'].includes(currency);
        const amount = cents / 100;

        return new Intl.NumberFormat('ja-JP', {
          style: 'currency',
          currency,
          maximumFractionDigits: usesMinorUnits ? 2 : 0,
        }).format(amount);
      }

      updateTotal() {
        const unitPrice = this.getUnitPrice();
        const quantity = Math.max(1, Number(this.quantityInput?.value || 1));
        const text = unitPrice == null ? '選択中プランの価格 × 3回' : this.formatMoney(unitPrice * quantity * 3);

        this.querySelectorAll('[data-subscription-total]').forEach((element) => {
          if (element.textContent !== text) element.textContent = text;
        });
      }

      handleChange() {
        if (this.checkbox?.checked) this.error.hidden = true;
        this.sync();
      }

      handleSubmit(event) {
        if (!this.isSubscriptionSelected() || this.checkbox?.checked) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.error.hidden = false;
        this.openButton?.focus();
      }

      handleDialogCancel(event) {
        event.preventDefault();
        this.closeDialog();
      }

      stopInternalPropagation(event) {
        event.stopPropagation();
      }

      openDialog() {
        if (typeof this.dialog?.showModal === 'function') {
          this.dialog.showModal();
          document.body.classList.add('subscription-consent-dialog-open');
        } else {
          this.dialog?.setAttribute('open', '');
        }
      }

      closeDialog() {
        if (this.dialog?.open && typeof this.dialog.close === 'function') {
          this.dialog.close();
        } else {
          this.dialog?.removeAttribute('open');
        }
        document.body.classList.remove('subscription-consent-dialog-open');
        this.openButton?.focus();
      }

      confirmReview() {
        this.hasReviewed = true;
        this.checkbox.disabled = false;
        this.closeDialog();
        this.checkbox.focus();
      }

      sync() {
        const subscriptionSelected = this.isSubscriptionSelected();
        this.hidden = !subscriptionSelected;

        if (!subscriptionSelected) {
          this.checkbox.checked = false;
          this.error.hidden = true;
          this.consentProperty.disabled = true;
          return;
        }

        this.checkbox.disabled = !this.hasReviewed;
        this.consentProperty.disabled = !this.checkbox.checked;
        this.updateTotal();
      }
    }
  );
}
